"use strict"

import React from "react"
import { extent } from "d3-array"
import memoize from "memoize-one"
import deepEqual from "deep-equal"
import { Button, ButtonGroup } from "react-bootstrap"

import { arrayWithContents, isNumber } from "../../utils"
import { VerticalPlotDashboard } from "../AbstractDashboards/AbstractDashboard"
import { getSiteReactors, makeXAxis, toggleSingleMultiRunPlot } from "../../graphing/resources/utils"
import NoGraphingDataMessage from "../../graphing/plotting/widgets/NoGraphingDataMessage"
import DraggableGraphHeader from "../../graphing/resources/DraggableGraphHeader"
import DefaultGraphConfigButton from "../../graphing/resources/DefaultGraphConfigButton"
import ZoomControls from "../../graphing/zooming/ZoomControls"
import ZoomControlsPopover from "../../graphing/zooming/ZoomControlsPopover"
import PerGraphZoomScrollButtons from "../../graphing/zooming/ZoomControls/PerGraphZoomScrollButtons"
import KeyBody from "../../graphing/plotting/GraphKeys/KeyBody"
import KeyHeader from "../../graphing/plotting/GraphKeys/KeyHeader"
import ToggleAnnotationsButton from "../../graphing/plotting/widgets/ToggleAnnotationsButton"
import ToggleRunLabelsButton from "../../graphing/plotting/widgets/ToggleRunLabelsButton"
import GraphImageModal, { GraphImageLegend } from "../../graphing/modals/GraphImageModal"
import DataAndCommentExportModals from "../../graphing/modals/DataAndCommentExportModals"
import DownloadPopoverMenu from "../../graphing/plotting/widgets/DownloadPopoverMenu"
import ExcelTravelerButton from "../../graphing/modals/ExcelTravelerButton"
import FavouritesSelector from "../../favourites/FavouritesSelector"
import RunInfoHeader from "./RunInfoHeader"
import AddAnotherPlotBtn from "./AddAnotherPlotBtn"
import FontEditPopover from "../../graphing/plotting/widgets/FontEditPopover"

import {
  isLTPM,
  id_accessor,
  single_run_view,
  getFormattedMessage,
} from "../../../components/shared_components_registration"
import ReportGenerationButton from "../../graphing/modals/ReportGenerationModal/ReportGenerationButton"
const registration = {
  select_trends_msg: (intl) =>
    isLTPM
      ? intl.formatMessage({ id: "graphing.select.tags.to.compare" })
      : "Choose a data-series from the menu to the right",
  ...single_run_view,
}
const { SkynaughtAnnotationsMenu, RunFilesDownloader, SettingsButtons } = registration

export default class SingleRunView extends VerticalPlotDashboard {
  static defaultProps = {
    consider_key_width: true, // Used for resizing and widthSensor
    plot_settings: {
      height: 350,
      margin: {
        top: 22.5,
        bottom: 80,
        left: 70,
        right: 80,
      },
      smallWidthHeight: 250,
    },
    chartWidthHeightChange: 500,
    keyWidth: 325,
  }

  constructor(props) {
    super(props)

    this.state = {
      width: 600,
      margins_adjustment: 160 /* FIXME hard-coded margins here */,
      graph_variables: {
        graphs: "graphs",
        updateGraphs: "updateGraphs",
      },
    }

    this._getPlotProps = this._getPlotProps.bind(this)
    this._getCrossPlotProps = this._getCrossPlotProps.bind(this)
    this.toggleSingleMultiRunPlot = toggleSingleMultiRunPlot.bind(this)
    this.updateGraphsForSingle = this.updateGraphsForSingle.bind(this)
  }

  updateGraphsForSingle() {
    const { graphs, updateGraphs } = this.props

    // Clean-up - If any plots are 'multi' toggle them to single for this view
    if (graphs.filter((op) => op?.special === "multi").length > 0) {
      let newGraphs = Object.assign([], graphs)

      graphs.forEach((op, index) => {
        op?.special === "multi" &&
          this.toggleSingleMultiRunPlot(
            index,
            op,
            (newGraph, i) => (newGraphs[i] = newGraph),
            true, // multi: plot is special === "multi"
          )
      })

      !deepEqual(newGraphs, graphs) && updateGraphs(newGraphs)
    }
  }

  componentDidMount() {
    this.standardDidMount()

    const { runs, runData } = this.props

    this.updateGraphsForSingle()
    this.ensureDataRequested(runs, runData, this._plotFactoryCache, false)
  }

  componentDidUpdate(prevProps, prevState) {
    const { runs, runData, graphs } = this.props

    if (runData !== prevProps.runData || graphs !== prevProps.graphs) {
      this.updateGraphsForSingle()
    }

    this.ensureDataRequested(runs, runData, this._plotFactoryCache, false)
  }

  componentWillUnmount() {
    this.standardWillUnmount()
  }

  // Only passing selectedMutation, frequencyThreshold to check for changes in memoized function
  _getPlotProps(run, plotFactories, timeExtractor, selectedMutation, frequencyThreshold) {
    const viewerState = { ...this.props, ...this.state }
    const timeRanges = []
    const plotProps = plotFactories.map(({ plotBaton: baton }) => {
      if (baton && baton.getPlotProps) {
        const timeRange = baton.getAxesAndRanges(run, viewerState, timeExtractor)["time"]
        if (timeRange) {
          timeRanges.push(timeRange[0])
          timeRanges.push(timeRange[1])
        }

        return baton.getPlotProps(run, viewerState)
      } else {
        return {}
      }
    })

    return [plotProps, timeRanges]
  }

  _getCrossPlotProps(plotFactories, plotProps) {
    return plotFactories.map(({ plotFactory: plot }, index) => {
      if (plot && plot.getCrossPlotProps && plotProps[index]) {
        return plot.getCrossPlotProps([plotProps[index]])
      } else {
        return {}
      }
    })
  }

  /* Memoize functions to remember the last passed arguments and result.
   *  If a function is next called with the same arguments then the previous result is returned without
   *  rerunning the function unnecessarily.  Passing in deepEqual to check equality of more complex arguments. */
  makePlotList = memoize(registration.makePlotList, deepEqual)
  getPlotProps = memoize(this._getPlotProps, deepEqual)
  getCrossPlotProps = memoize(this._getCrossPlotProps, deepEqual)

  render() {
    const viewerState = { ...this.props, ...this.state }
    const {
      runs,
      timeExtractor,
      zoomConfig,
      graphs,
      updateGraphs,
      special_graphs,
      intl,
      queryTags = [],
      report_metrics = [],
      width,
      chartWidthHeightChange,
      plot_settings,
      minTime,
      maxTime,
      keyWidth,
      selectedMutation, // Mutation Plot Related Var - LanzaLake
      frequencyThreshold, // Mutation Plot Related Var - LanzaLake
    } = viewerState

    const plotFactories = this.getPlotFactories(graphs, updateGraphs, this._plotFactoryCache, true)
    this._plotFactoryCache = plotFactories

    const availableRuns = this.getAvailableRuns(runs, this.props.runData),
      singleRunData = availableRuns[0] || {},
      {
        inoculated_on,
        forceReload,
        err,
        fileList = [],
        observationTypes = {},
        observationTypesFlat = {},
        actions = [],
        site_actions = [],
        annotations = [],
      } = singleRunData,
      run_id = singleRunData[id_accessor],
      { height, margin, smallWidthHeight } = plot_settings,
      exportBaseName = registration.getExportBaseName(singleRunData),
      [plotProps, timeRanges] = this.getPlotProps(
        singleRunData,
        plotFactories,
        timeExtractor,
        selectedMutation, // To trigger a plot prop reload on change - not used in memoized method
        frequencyThreshold, // To trigger a plot prop reload on change - not used in memoized method
      ),
      [minFullTime, maxFullTime] = extent(timeRanges),
      crossPlotProps = this.getCrossPlotProps(plotFactories, plotProps),
      plotHeight = width < chartWidthHeightChange ? smallWidthHeight : height,
      plotList = this.makePlotList(special_graphs, queryTags, graphs, [observationTypes || {}])

    this.baseTime = timeExtractor.getOriginTime(inoculated_on, minFullTime) // Stash for use by zooming code

    const x = makeXAxis(
      timeExtractor,
      timeExtractor.offsetDays(this.baseTime, minTime || 0),
      maxTime || isNumber(maxTime) ? timeExtractor.offsetDays(this.baseTime, maxTime) : maxFullTime,
      0,
      width,
    )

    const timeWarning =
      timeExtractor.difference(x.domain()[0], maxFullTime) > 0 ||
      timeExtractor.difference(x.domain()[1], minFullTime) < 0

    const ReloadRunBtn = () => (
      <Button
        variant={"outline-primary"}
        title={getFormattedMessage("general.refresh", "Refresh", true, intl)}
        onClick={forceReload}
      >
        <span className={"fa fa-refresh"} />
      </Button>
    )

    const combinedActions = singleRunData.getCombinedActions
      ? singleRunData.getCombinedActions(singleRunData)
      : []

    const siteReactors = getSiteReactors(this.props.runData, runs)

    return (
      <>
        {err ? (
          <div style={{ display: "flex" }}>
            <div className={"error-message"}>{err.toString()}</div>
            <div style={{ marginLeft: "auto" }}>
              <ReloadRunBtn />
            </div>
          </div>
        ) : (
          <div style={{ paddingTop: "10px", paddingBottom: "100px" }}>
            {this.state.dragPlotMoved && (
              <DraggableGraphHeader
                plot={graphs[this.state.dragPlotIndex]}
                portal={this.dragObjectHolder}
                px={this.state.dragPlotCurrentX - this.state.dragPlotOffsetX}
                py={this.state.dragPlotCurrentY - this.state.dragPlotOffsetY}
                panelWidth={keyWidth}
              />
            )}
            <div className="row" style={{ marginBottom: "10px" }}>
              <div className="col-8">
                <RunInfoHeader runData={singleRunData} intl={intl} />
              </div>
              <div className="col-4" align="right">
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-end",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {arrayWithContents(graphs) && (
                      <div className={"zoom-controls"}>
                        <ZoomControls
                          intl={intl}
                          axesAndRanges={
                            !isNaN(this.baseTime) && !isNaN(maxFullTime)
                              ? { time: [this.baseTime, maxFullTime] }
                              : {}
                          }
                          universalXZoomOnly={true}
                          zoomKey={run_id}
                          baseTime={this.baseTime}
                        />
                      </div>
                    )}
                  </div>
                  &nbsp;
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <DefaultGraphConfigButton intl={intl} button={true} type={"graphs"} />

                    <FavouritesSelector
                      intl={intl}
                      runs={runs}
                      view={"Standard"}
                      graphSet={graphs}
                      loadGraphSet={updateGraphs}
                      updateRunList={this.props.updateRunList}
                      should_match_view={false}
                      dropToggleClass={"width-100"}
                    />
                    <SettingsButtons intl={intl} />
                    <FontEditPopover intl={intl} />
                    <ExcelTravelerButton runData={singleRunData} intl={intl} isBtn={true} />
                    <RunFilesDownloader fileList={fileList} exportBaseName={exportBaseName} />
                    <ReportGenerationButton
                      availableRuns={availableRuns}
                      plotList={plotList}
                      siteMetrics={report_metrics}
                      siteReactors={siteReactors}
                      intl={intl}
                    />
                    <ReloadRunBtn />
                  </div>
                </div>
              </div>
            </div>
            {timeWarning && <NoGraphingDataMessage intl={intl} />}
            {/* Sense width of page */}
            <div style={{ display: "flex" }}>
              <div style={{ flex: 1 }} ref={this.widthSensorRef} />
              <div style={{ flex: `${keyWidth}px 0 0`, width: `${keyWidth}px` }} />
            </div>
            <SkynaughtAnnotationsMenu runID={run_id} width={keyWidth} />
            <div>
              {arrayWithContents(graphs) ? (
                graphs.map((op, opIndex) => {
                  const plotEntry = plotFactories[opIndex]
                  const plotFactory = plotEntry.plotFactory
                  const perGraphZoom = zoomConfig.zoomForPlot(run_id, op)
                  let content
                  if (plotFactory) {
                    const axesAndRanges = Object.assign(
                      {},
                      plotEntry.plotBaton.getAxesAndRanges(singleRunData, viewerState, timeExtractor),
                    )
                    if (axesAndRanges.time) {
                      axesAndRanges.time = [this.baseTime, maxFullTime]
                    }

                    const modalName = `${op.title}_modal_${opIndex}` // Unique modal identifier
                    const dataModalName = `Data_${modalName}`
                    const commentsModalName = `Comments_${modalName}`

                    const xAxisVariables = {
                      baseTime: this.baseTime,
                      minTime: minTime,
                      maxTime: maxTime,
                      single: true, // Used in GraphImageModal to make X-Axis scale decision
                      offset: null,
                      maxFullTime: maxFullTime,
                    }

                    // Variables passed to both GraphImageModal and plotFactory.Component
                    const sharedGraphSettings = {
                      zoomKey: run_id,
                      axesAndRanges: axesAndRanges,
                      x: x,
                      zoomRanges: perGraphZoom,
                      timeExtractor: timeExtractor,
                      width: width,
                      graphKey: op,
                      annotations: annotations,
                      actions: actions,
                      site_actions: site_actions,
                      runEventLabels: singleRunData.runEventLabels || [],
                      runInoculationDate: singleRunData?.inoculated_on,
                      ...xAxisVariables,
                    }

                    const plotProp = plotProps[opIndex]
                    const crossPlotProp = crossPlotProps[opIndex]

                    content = (
                      <div style={{ width: `${width + this.state.margins_adjustment}px` }}>
                        {/*---------- Controls ----------*/}
                        <ButtonGroup>
                          <ZoomControlsPopover
                            intl={intl}
                            placement={"right"}
                            axesAndRanges={axesAndRanges}
                            zoomKey={run_id}
                            graphKey={op}
                            graphIndex={opIndex}
                            plotConfigUpdater={plotEntry.plotConfigUpdater}
                            baseTime={this.baseTime}
                          />
                          <DownloadPopoverMenu
                            intl={intl}
                            placement={"right"}
                            graph_name={modalName}
                            data_name={dataModalName}
                            comment_name={commentsModalName}
                            actions={combinedActions}
                            loading={availableRuns.filter((r) => r.loading)}
                            op={op}
                            showGraphModal={this.showGraphModal}
                            popover={false}
                          />
                          <ToggleAnnotationsButton
                            annotations={annotations}
                            placement={"bottom"}
                            plotList={plotList}
                            exportBaseName={exportBaseName}
                            run={singleRunData}
                            intl={intl}
                          />
                          <ToggleRunLabelsButton
                            run={singleRunData}
                            exportBaseName={exportBaseName}
                            op={op}
                            plotImageSettings={xAxisVariables}
                            PlotImage={(width, height, x) => {
                              const { runEventLabels, ...graphSettings } = sharedGraphSettings
                              return (
                                <>
                                  <plotFactory.Component
                                    {...graphSettings}
                                    {...plotProp}
                                    {...crossPlotProp}
                                    x={x}
                                    width={width}
                                    height={height}
                                    margin={{ top: 20, bottom: 80, left: 85, right: 90 }}
                                    useSVG
                                  />
                                  <div style={{ display: "flex", width: "100%", justifyContent: "center" }}>
                                    <GraphImageLegend
                                      intl={intl}
                                      graphKey={op}
                                      graphIndex={opIndex}
                                      plotEntry={plotEntry}
                                      observationTypes={observationTypesFlat}
                                      crossPlotProps={crossPlotProp}
                                      width={"90%"}
                                    />
                                  </div>
                                </>
                              )
                            }}
                          />
                          {this.state[modalName] && (
                            <GraphImageModal
                              intl={intl}
                              onHide={() => this.hideGraphModal(modalName)}
                              plotEntry={plotEntry}
                              observationTypes={observationTypesFlat}
                              title={`${op.title} - ${exportBaseName}`}
                              graphIndex={opIndex}
                              plotProps={plotProp}
                              crossPlotProps={crossPlotProp}
                              {...sharedGraphSettings}
                            />
                          )}
                          {!!(this.state[dataModalName] || this.state[commentsModalName]) && (
                            <DataAndCommentExportModals
                              showDataModal={this.state[dataModalName]}
                              showCommentsModal={this.state[commentsModalName]}
                              onHideDataModal={() => this.hideGraphModal(dataModalName)}
                              onHideCommentsModal={() => this.hideGraphModal(commentsModalName)}
                              actions={combinedActions}
                              perGraphZoom={perGraphZoom}
                              commonDataVars={[singleRunData, timeExtractor, ...x.domain()]}
                              plotFactory={plotFactory}
                              exportBaseName={exportBaseName}
                              op={op}
                              viewerState={{ ...this.props, ...this.state }}
                            />
                          )}
                        </ButtonGroup>
                        {/*---------- /Controls ----------*/}

                        <PerGraphZoomScrollButtons
                          intl={intl}
                          axesAndRanges={axesAndRanges}
                          zoomRanges={perGraphZoom}
                          zoomConfig={zoomConfig}
                          extraStyle={{ margin: "0" }}
                        />

                        <plotFactory.Component
                          height={plotHeight}
                          margin={margin}
                          exportBaseName={exportBaseName}
                          forceReloadPlotData={() => this.forceReloadPlotData(plotEntry)}
                          {...sharedGraphSettings}
                          {...plotProp}
                          {...crossPlotProp}
                        />
                      </div>
                    )
                  } else {
                    content = (
                      <div
                        style={{
                          width: width + this.state.margins_adjustment,
                          height: `${plotHeight}px`,
                        }}
                      >
                        {registration.select_trends_msg(intl)}
                      </div>
                    )
                  }

                  return (
                    <div
                      key={opIndex}
                      style={{ display: "flex" }}
                      ref={(ref) => (this.plotRowRefs[opIndex] = ref)}
                    >
                      {content}
                      <div style={{ flex: `${keyWidth}px 0 0` }}>
                        <div className={"plot-key-div"}>
                          <div onMouseDown={this.plotDragStart.bind(this, opIndex)}>
                            <KeyHeader
                              intl={intl}
                              placement={"bottom"}
                              index={opIndex}
                              updateGraphs={updateGraphs}
                              plotList={plotList}
                              title={op?.title || registration.choose_graph_label(intl)}
                              graphNum={graphs.length}
                              siteReactors={siteReactors}
                            />
                          </div>
                          <KeyBody
                            intl={intl}
                            graphKey={op}
                            index={opIndex}
                            plotEntry={plotEntry}
                            graphs={graphs}
                            updateGraphs={updateGraphs}
                            observationTypes={observationTypesFlat}
                            crossPlotProps={crossPlotProps[opIndex]}
                            availableRuns={availableRuns}
                            toggleAvailable={!perGraphZoom.y && !perGraphZoom.y2}
                            timeExtractor={timeExtractor}
                            baseTime={this.baseTime}
                            axesAndRanges={
                              plotEntry?.plotBaton
                                ? Object.assign(
                                    {},
                                    plotEntry.plotBaton.getAxesAndRanges(
                                      singleRunData,
                                      viewerState,
                                      timeExtractor,
                                    ),
                                  )
                                : {}
                            }
                            siteReactors={siteReactors}
                          />
                        </div>
                      </div>
                    </div>
                  )
                })
              ) : (
                <h6 style={{ marginBottom: "150px", marginTop: "30px" }}>
                  *{registration.choose_graph_label(intl)}
                </h6>
              )}
              <AddAnotherPlotBtn intl={intl} updateGraphs={updateGraphs} />
            </div>
          </div>
        )}
      </>
    )
  }
}
